/**
 * ACCOUNT, at `/account` (slice A1): the page about her, which the header's Account button leads to
 * from every signed-in screen. No artboard in `faithfully-kept-business/design/home/` draws it yet;
 * it is built from the Settings canvas's Settings-Main artboard, drawn in today's look, in the
 * current tokens and parts, and goes on business issue 13's list. The words are `ACCOUNT` in
 * `words.ts`, all NEW for the owner to read but where they say otherwise.
 *
 * - WHO SHE IS: "Account" over her name as the office typed it, or "Your account"; for a member,
 *   what she can see, by her homes' labels, and that invoices are not shown to her; then the
 *   address she signed in with and her number by its last four, as the Worker cut them, or where
 *   she signed in by her mobile number that number in full and her address cut, and never anybody
 *   else's; and how to change any of them (round six, slice S2): the ask on People and sign-in
 *   ("Sign-in" for a member) as a link, where the Worker offers it, its fresh code emailed to her
 *   or texted where she signed in by number (home #11); her own ask while it waits, sent or with
 *   the office; else the phone alone. Ops's section 12.4 takes her own ask
 *   (`person.contact_change`), the office's to decide.
 * - ONE CARD OF TWO ROWS, each a link with the chevron, to its own page, with a live line: How you
 *   pay, a client's alone, says what Payments says of the saved method and her Stop
 *   (`lib/standing.ts`), never more than ops has said (binding rule 1), or that she pays each
 *   invoice herself, but not while an owed invoice is being charged now (slice A4), where it says
 *   nothing more than its name; People and sign-in names the household's people by their first
 *   names for a client, and says how many phones and computers she is signed in on. A member's row
 *   is Sign-in, and names nobody. A third, What we send you, for a client and a member alike, only
 *   once the Worker says ops has pushed her choices of what we send her (ops's section 11.3, P114):
 *   until then nothing new shows.
 * - HER REGULAR CLEANS: each home's cadence and weekday as Schedule's heading words them, a pause
 *   that has not ended, "Paused." where ops holds the schedule paused with no days left to say
 *   (P1), "No regular cleans." for a home with none, each home named by its label when she sees
 *   several; never a word about the regular cleans from the household's own `status`, which decides
 *   nothing about them (ops's 11.6, question 1, P1); and for a client, under
 *   each home, what she may ask the office about them, to pause, resume, change or end them, and
 *   where the household's asks stand (round five, slice C3, ops's section 12.4,
 *   `components/CleansAsks.tsx`), with the way to call said as the other way to ask, or as the one
 *   way where no home offers an ask. Nothing reads paused, changed or ended until ops pushes it so
 *   (binding rule 1). Never the window: two times on a page about the standing schedule would read
 *   as how long a clean lasts. Under them, for a client and a member, the way to Your agreement
 *   (round five, slice C2).
 * - HELP: the office's number on a button of its own, so it can be read at a desk, the hours, the
 *   office's email, and the website's privacy, terms and cancellation and re-clean policy pages,
 *   which no other signed-in screen links to.
 * - SIGN OUT at the foot, moved here from Home's foot, so a shared phone can be handed back signed
 *   out one tap from any screen (`components/SignOut.tsx`). A read that does not go through draws
 *   the page's heading and the line that says so, with the phone, and still Help and Sign out,
 *   since neither needs the read.
 * - No tab of the nav is the place shown here; the header's Account button is (`Header.tsx`). A 401
 *   goes to the door and a 404 to the page that says to call, as on every place.
 */
import { useId, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  getAccount,
  type AccountPlace,
  type HomeCleans,
  type RegularCleans,
  type SavedMethod,
  type You,
} from '../api.ts';
import { GHOST_BUTTON, INLINE_LINK, NAVY_BUTTON, TEXT_LINK } from '../components/buttons.ts';
import { Backward, Onward } from '../components/Chevron.tsx';
import { useCleansAsks, type CleansAsking } from '../components/CleansAsks.tsx';
import { FOOTER_LINK } from '../components/Footer.tsx';
import { Guarded, type Signed } from '../components/Guarded.tsx';
import { Phone } from '../components/Phone.tsx';
import { Shell } from '../components/Shell.tsx';
import { SignOutFailed, useSignOut } from '../components/SignOut.tsx';
import {
  EMAIL_HREF,
  HOURS,
  PHONE_HREF,
  POLICY_HREF,
  PRIVACY_HREF,
  TERMS_HREF,
} from '../contact.ts';
import { cleansLine } from '../lib/cleans.ts';
import { shortDate } from '../lib/dates.ts';
import { numberShown } from '../lib/identifier.ts';
import { countInWords } from '../lib/marks.ts';
import { standing } from '../lib/standing.ts';
import {
  ACCOUNT_AGREEMENT_PATH,
  ACCOUNT_PATH,
  ACCOUNT_PAYING_PATH,
  ACCOUNT_PEOPLE_PATH,
  ACCOUNT_SENDING_PATH,
} from '../paths.ts';
import {
  ACCOUNT,
  AGREEMENT,
  chargesAutomatically,
  CLEANS,
  FAILED_BEFORE_PHONE,
  memberSees,
  mobileEnding,
  PEOPLE,
  peopleNamed,
  REQUEST,
  REST_ON_ITS_WAY,
  SENDING,
  SIGN_OUT,
  signedInOn,
  STOP,
} from '../words.ts';

export const EYEBROW = 'font-mono text-small tracking-widest text-text-emphasis uppercase';
export const PAGE_HEADING = 'font-serif text-h2 font-semibold text-pretty text-text-body';

export function Account(signed: Signed) {
  return (
    <Guarded
      load={getAccount}
      signed={signed}
      at={null}
      heading={ACCOUNT.name}
      failed={<AccountFailed signed={signed} />}
    >
      {(place, reload) => <AccountDrawn place={place} signed={signed} reload={reload} />}
    </Guarded>
  );
}

/** Account when its read did not go through: the heading, the line, and what needs no read. */
function AccountFailed({ signed }: { readonly signed: Signed }) {
  return (
    <Shell at={null}>
      <div>
        <h1 className={PAGE_HEADING}>{ACCOUNT.name}</h1>
        <p className="mt-2 text-body text-pretty">
          {FAILED_BEFORE_PHONE} <Phone after="." />
        </p>
      </div>
      <Help />
      <SignOutAtTheFoot signed={signed} />
    </Shell>
  );
}

function AccountDrawn({
  place,
  signed,
  reload,
}: {
  readonly place: AccountPlace;
  readonly signed: Signed;
  readonly reload: () => void;
}) {
  const { you } = place;
  const asks = useCleansAsks({
    homes: place.cleans ?? [],
    household: place.household.id,
    today: place.today,
    reload,
    onSignedOut: signed.onSignedOut,
  });
  return (
    <Shell at={null} payments={place.household.payments} over={asks.over}>
      <div>
        <p className={EYEBROW}>{ACCOUNT.name}</p>
        <h1 className={`mt-1 ${PAGE_HEADING}`}>{you.name ?? ACCOUNT.yourAccount}</h1>
        {you.role === 'member' && !place.arriving.household && you.homes?.length !== 0 ? (
          <p className="mt-2 text-body text-pretty">
            {memberSees(you.homes)} {ACCOUNT.noInvoices}
          </p>
        ) : null}
        <Details you={you} />
        <ChangeDetails place={place} />
      </div>

      <div
        data-account-rows
        className="overflow-hidden rounded-card border border-border-subtle bg-surface-raised"
      >
        {place.household.payments ? (
          <Row to={ACCOUNT_PAYING_PATH} title={ACCOUNT.howYouPay} lines={payingLinesOf(place)} />
        ) : null}
        <Row
          to={ACCOUNT_PEOPLE_PATH}
          title={peopleTitle(place)}
          lines={[peopleAndSignIn(place)]}
          divided={place.household.payments === true}
        />
        {place.sending ? (
          <Row to={ACCOUNT_SENDING_PATH} title={SENDING.heading} lines={[SENDING.row]} divided />
        ) : null}
      </div>

      <RegularCleansCard place={place} asks={asks} />

      <Help />

      <SignOutAtTheFoot signed={signed} />
    </Shell>
  );
}

/** Her address and her number, as the Worker gives them: each row only when it has one. */
function Details({ you }: { readonly you: You }) {
  const mobile =
    you.mobile !== undefined
      ? numberShown(you.mobile)
      : you.mobileEnding !== undefined
        ? mobileEnding(you.mobileEnding)
        : undefined;
  if (you.email === undefined && mobile === undefined) return null;
  return (
    <dl className="mt-3 grid grid-cols-[4.25rem_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-small">
      {you.email === undefined ? null : (
        <>
          <dt className="text-text-muted">{ACCOUNT.email}</dt>
          <dd className="text-text-body [overflow-wrap:anywhere]">{you.email}</dd>
        </>
      )}
      {mobile === undefined ? null : (
        <>
          <dt className="text-text-muted">{ACCOUNT.mobile}</dt>
          <dd className="text-text-body">{mobile}</dd>
        </>
      )}
    </dl>
  );
}

/**
 * How to change her name, email or mobile (round six, slice S2): her own ask while it waits on the
 * office, in People and sign-in's words; else, where the Worker offers the ask, the way to it on
 * People and sign-in ("Sign-in" for a member), the one link in the line, since a second a line away
 * would take its taps (Call is in the header and in Help); else the phone alone, as before ops
 * grants her. The ask's fresh code goes to what she signed in with, emailed, or texted where she
 * signed in by her mobile number (home #11), so it is offered either way.
 */
function ChangeDetails({ place }: { readonly place: AccountPlace }) {
  const { you } = place;
  const waits = you.contactAsk?.state === 'sent' || you.contactAsk?.state === 'received';
  const line = 'mt-2 text-small text-pretty text-text-muted';
  if (waits) {
    return (
      <p className={line}>
        {PEOPLE.askedToChangeMine}{' '}
        {you.contactAsk?.state === 'sent' ? REQUEST.sent : REQUEST.waiting}
      </p>
    );
  }
  if (you.offers.includes('person.contact_change')) {
    return (
      <p className={line}>
        {ACCOUNT.changeAskIn}{' '}
        <span className="whitespace-nowrap">
          <Link to={ACCOUNT_PEOPLE_PATH} className={INLINE_LINK}>
            {peopleTitle(place)}
          </Link>
          .
        </span>
      </p>
    );
  }
  return (
    <p className={line}>
      {ACCOUNT.changeBeforePhone} <Phone after="." />
    </p>
  );
}

/** A row of the card: its words over its live lines, and the chevron, leading to its page. */
function Row({
  to,
  title,
  lines,
  divided = false,
}: {
  readonly to: string;
  readonly title: string;
  readonly lines: readonly string[];
  readonly divided?: boolean;
}) {
  return (
    <Link
      to={to}
      className={`flex min-h-14 items-center justify-between gap-3 px-5 py-4 hover:bg-surface-accent-soft ${
        divided ? 'border-t border-border-subtle' : ''
      }`}
    >
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-body font-semibold text-text-body">{title}</span>
        {lines.map((line) => (
          <span key={line} className="text-small text-pretty text-text-body">
            {' '}
            {line}
          </span>
        ))}
      </span>
      <Onward />
    </Link>
  );
}

/** The saved method in ops's words, "Charges automatically: Visa ending 4242, since 12 Oct". */
function chargesLine(method: SavedMethod): string {
  const since = method.agreedOn === undefined ? null : shortDate(method.agreedOn);
  return chargesAutomatically(method.label, method.methodType, since);
}

/**
 * How the household pays, as Payments says it (`lib/standing.ts`): the saved method, with "Sent to
 * the office." while her Stop is on its way; stopped, or nothing saved, once ops says so; or that
 * she pays each invoice herself, but never while the Worker says an owed invoice is being charged
 * now (`chargingNow`, slice A4), which a Stop does not cancel and that invoice's card on Payments
 * says, where the row says nothing more than its name, as Payments says nothing of it then. While
 * the household is still coming down, that the rest is on its way, since nothing yet says which.
 */
export function payingLines(
  paying: {
    readonly autopay?: SavedMethod | null;
    readonly stopAsk?: AccountPlace['stopAsk'];
    readonly chargingNow?: true;
  },
  arriving: boolean,
): string[] {
  if (arriving) return [REST_ON_ITS_WAY];
  const method = paying.autopay ?? null;
  const said = standing({ autopay: method, stopAsk: paying.stopAsk }, null, false);
  if (said === null || method === null) {
    return paying.chargingNow === true ? [] : [ACCOUNT.youPayEachInvoice];
  }
  switch (said.kind) {
    case 'offered':
    case 'changed':
      // A Stop ops refused as naming another method leaves the method as it stands, as a Stop that
      // did not go through does: Payments says what ops said of it.
      return [chargesLine(method)];
    case 'sent':
      return [chargesLine(method), REQUEST.sent];
    case 'stopped':
      return [STOP.stopped];
    case 'nothing_saved':
      return [STOP.nothingSaved];
  }
}

const payingLinesOf = (place: AccountPlace) => payingLines(place, place.arriving.household);

/** The second row's words: People and sign-in for a client, Sign-in for anyone else. */
export const peopleTitle = (place: AccountPlace): string =>
  place.household.payments ? ACCOUNT.peopleAndSignIn : ACCOUNT.signIn;

/**
 * The second row's line: for a client, the household's people by first name, or that she is the
 * only one; then how many phones and computers she is signed in on, "Signed in here only." for this
 * one alone.
 */
export function peopleAndSignIn(place: AccountPlace): string {
  const said: string[] = [];
  const people = place.household.payments ? place.people : undefined;
  if (people !== undefined) {
    const named = people.onlyYou ? ACCOUNT.onlyYou : peopleNamed(people.names, people.more);
    if (named !== null) said.push(named);
  }
  if (place.sessions === 1) said.push(ACCOUNT.signedInHereOnly);
  else if (place.sessions > 1) said.push(signedInOn(countInWords(place.sessions)));
  return said.join(' ');
}

/**
 * Her homes' regular cleans, in a card of their own; under each home, for a client, what she may ask
 * the office about them and where the household's asks stand (`CleansAsks.tsx`, round five, slice
 * C3); and the way to call, said as the other way to ask where a home offers one.
 */
function RegularCleansCard({
  place,
  asks,
}: {
  readonly place: AccountPlace;
  readonly asks: CleansAsking;
}) {
  const several = place.homes.length > 1;
  const cleans = new Map((place.cleans ?? []).map((home) => [home.id, home]));
  const offered = (place.cleans ?? []).some((home) => home.offers.length > 0);
  return (
    <section
      data-regular-cleans
      className="rounded-card border border-border-subtle bg-surface-raised px-5 py-5"
    >
      <h2 className="font-serif text-h3 font-semibold text-text-brand">{ACCOUNT.regularCleans}</h2>
      {place.arriving.homes ? (
        <p className="mt-2 text-body text-pretty">{REST_ON_ITS_WAY}</p>
      ) : (
        <div className="mt-1 divide-y divide-border-subtle">
          {place.homes.map((home) => (
            <Cleans
              key={home.id}
              home={home}
              named={several}
              asking={cleans.get(home.id)}
              asks={asks}
            />
          ))}
        </div>
      )}
      <Link to={ACCOUNT_AGREEMENT_PATH} className={`mt-1 gap-2 text-body ${TEXT_LINK}`}>
        {AGREEMENT.heading}
        <Onward />
      </Link>
      {place.household.payments ? (
        <p className="mt-3 text-small text-pretty text-text-muted">
          {offered ? CLEANS.alsoByPhoneBeforePhone : ACCOUNT.changeCleansBeforePhone}{' '}
          <Phone after="." />
        </p>
      ) : null}
    </section>
  );
}

function Cleans({
  home,
  named,
  asking,
  asks,
}: {
  readonly home: RegularCleans;
  readonly named: boolean;
  /** What she may ask about it, a client's alone. */
  readonly asking: HomeCleans | undefined;
  readonly asks: CleansAsking;
}) {
  const labelId = useId();
  const line = cleansLine(home);
  const labelled = named && home.label !== undefined;
  return (
    <div className="py-3" {...(asking ? { 'data-cleans-home': home.id } : {})}>
      {labelled ? (
        <h3 id={labelId} className={EYEBROW}>
          {home.label}
        </h3>
      ) : null}
      {line === null ? null : <p className="text-body text-pretty text-text-body">{line}</p>}
      {asking ? asks.block(asking, labelled ? labelId : undefined) : null}
    </div>
  );
}

/** How to reach the office, and the website's own pages. */
function Help() {
  const pages: readonly [string, string][] = [
    [ACCOUNT.privacy, PRIVACY_HREF],
    [ACCOUNT.terms, TERMS_HREF],
    [ACCOUNT.policy, POLICY_HREF],
  ];
  return (
    <section data-help className="border-t border-border-subtle pt-5">
      <h2 className="font-serif text-h3 font-semibold text-text-body">{ACCOUNT.help}</h2>
      <a href={PHONE_HREF} className={`mt-3 ${NAVY_BUTTON}`}>
        {ACCOUNT.call}
      </a>
      <p className="mt-2 text-small text-text-muted">{HOURS}</p>
      <a href={EMAIL_HREF} className={`mt-1 text-body ${TEXT_LINK}`}>
        {ACCOUNT.emailUs}
      </a>
      <ul className="flex flex-wrap gap-x-6">
        {pages.map(([words, href]) => (
          <li key={href}>
            <a href={href} className={FOOTER_LINK}>
              {words}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Sign out, at the foot, with the line that says when it did not go through. */
function SignOutAtTheFoot({ signed }: { readonly signed: Signed }) {
  const leaving = useSignOut(signed.onSignedOut);
  return (
    <div>
      <button type="button" onClick={leaving.signOut} className={`cursor-pointer ${GHOST_BUTTON}`}>
        {SIGN_OUT}
      </button>
      <SignOutFailed failed={leaving.failed} />
    </div>
  );
}

/**
 * The way back from Account's own pages: a chevron pointing back, and the words underlined, a box
 * 48 tall. The page's eyebrow and heading follow it.
 */
export function BackToAccount() {
  return (
    <Link to={ACCOUNT_PATH} className={`gap-1 text-body ${TEXT_LINK}`}>
      <Backward />
      {ACCOUNT.backToAccount}
    </Link>
  );
}

/** One of Account's own pages: the way back, "Account" over its heading, and what it draws. */
export function AccountPage({
  heading,
  children,
}: {
  readonly heading: string;
  readonly children?: ReactNode;
}) {
  return (
    <div>
      <BackToAccount />
      <p className={`mt-3 ${EYEBROW}`}>{ACCOUNT.name}</p>
      <h1 className={`mt-1 ${PAGE_HEADING}`}>{heading}</h1>
      {children}
    </div>
  );
}
